import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

interface DateAvailabilityDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (date: string | null) => void;
  currentDate: string | null;
  vehicleId?: string;
  vehicleInfo?: string;
}

const DateAvailabilityDialog: React.FC<DateAvailabilityDialogProps> = ({
  open,
  onClose,
  onConfirm,
  currentDate,
  vehicleId,
  vehicleInfo,
}) => {
  const [selectedDate, setSelectedDate] = React.useState<string | null>(currentDate);
  const [isTBD, setIsTBD] = React.useState<boolean>(currentDate === 'TBD');
  const [error, setError] = React.useState<string | null>(null);

  // Reset the selected date when the dialog opens with a new currentDate
  React.useEffect(() => {
    if (open) {
      setSelectedDate(currentDate);
      setIsTBD(currentDate === 'TBD');
      setError(null);
    }
  }, [currentDate, open]);

  const handleConfirm = () => {
    // Require either a date selection or TBD to be checked
    if (!selectedDate && !isTBD) {
      setError('Please select a date or mark as TBD before confirming');
      return;
    }
    
    const finalValue = isTBD ? 'TBD' : selectedDate;
    onConfirm(finalValue);
    onClose();
  };

  // Determine dialog title and message based on whether we have an existing date
  const isEditing = currentDate !== null;
  const dialogTitle = isEditing 
    ? "Edit Availability Date" 
    : "When will this vehicle be available?";
  
  const helperText = isEditing
    ? "You can modify the date when this vehicle will be available"
    : "Select the date when this vehicle will be available";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      aria-labelledby="date-availability-dialog-title"
    >
      <DialogTitle 
        id="date-availability-dialog-title"
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <CalendarTodayIcon />
        <Typography variant="h6">{dialogTitle}</Typography>
      </DialogTitle>
      
      
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {isEditing && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Current availability date:
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {currentDate === 'TBD' ? 'To Be Determined' : dayjs(currentDate).format('MMMM D, YYYY')}
              </Typography>
            </Box>
          )}
          
          <FormControlLabel
            control={
              <Checkbox
                checked={isTBD}
                onChange={(e) => {
                  setIsTBD(e.target.checked);
                  if (e.target.checked) {
                    setSelectedDate(null);
                  }
                  if (error) setError(null);
                }}
              />
            }
            label="To Be Determined"
            sx={{ mb: 2 }}
          />
          
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date Available"
              value={selectedDate && selectedDate !== 'TBD' ? dayjs(selectedDate) : null}
              onChange={(newDate) => {
                setSelectedDate(newDate ? newDate.toISOString() : null);
                if (newDate) setIsTBD(false);
                if (error) setError(null);
              }}
              disablePast
              disabled={isTBD}
              slotProps={{
                textField: {
                  fullWidth: true,
                  helperText: isTBD ? "Date selection disabled when TBD is selected" : helperText,
                  error: !!error,
                },
              }}
            />
          </LocalizationProvider>
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleConfirm} 
          color="primary" 
          variant="contained"
          disabled={!selectedDate && !isTBD}
        >
          {isEditing ? "Update" : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DateAvailabilityDialog; 